import { orderBy } from 'lodash';
import { useCallback, useRef } from 'react';

import { type Clip } from '@air/api/types';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { ClipUtils } from '@air/utils-clips';

import { useAddAssetsToBoardsModal } from '~/components/Modals/AddAssetsToBoardsModal/useAddAssetsToBoardsModal';
import { DeletePrivateClipsModal } from '~/components/Modals/DeletePrivateClipsModal';
import { EditClipsTagsModal } from '~/components/Modals/EditClipsTagsModal';
import { MoveItemsModal } from '~/components/Modals/MoveItemsModal/MoveItemsModal';
import { useRemoveAssetsFromBoardModal } from '~/components/Modals/RemoveClipsFromBoardModal/hooks/useRemoveAssetsFromBoardModal';
import { SetBoardThumbnailModal } from '~/components/Modals/SetBoardThumbnail/SetBoardThumbnailModal';
import { SharePrivateClipModal } from '~/components/SharePrivateClipModal/SharePrivateClipModal';
import { PrivateContextSourcesAddClipModal } from '~/components/v2/components/context-sources/modals/PrivateContextSourcesAddClipModal';
import { useCanEditContextSources } from '~/components/v2/hooks/useCanEditContextSources';
import { type GetItemMenuOptionsCallback } from '~/constants/MenuOptions';
import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useAddToCollectionOption } from '~/hooks/useAddToCollectionOption';
import { useAssetPermissionsCache } from '~/hooks/useAssetPermissionsCache';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCanShowInFinderOption } from '~/hooks/useCanShowInFinderOption';
import { useClipPermissionsCache } from '~/hooks/useClipPermissionsCache';
import { useDownloadPrivateAsset } from '~/hooks/useDownloadPrivateAsset';
import { useDownloadPrivateProxyAsset } from '~/hooks/useDownloadPrivateProxyAsset';
import { useGetPrivateCustomFieldsMenuOptions } from '~/hooks/useGetCustomFieldsMenuOptions';
import { useGetPrivateClipMenuOptions } from '~/hooks/useGetPrivateClipMenuOptions';
import { useGoToAssetPage } from '~/hooks/useGoToAssetPage';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useDesktopAppConnectionContext } from '~/providers/DesktopAppConnectionProvider';
import { CentralizedBoardSelectors, centralizedBoardTitleSelector } from '~/store/centralizedBoard/selectors';
import { useToggleFavorite } from '~/swr-hooks/clips/useToggleFavorite';
import {
  canAddAssetToBoard,
  canChangeAssetCustomFields,
  canChangeVersionDetails,
  canDeleteAsset,
  canDownloadVersion,
  canEditClipTags,
  canMoveAsset,
  canRemoveClipTags,
} from '~/utils/permissions/assetPermissions';
import { canRemoveAssetsFromBoard } from '~/utils/permissions/boardPermissions';
import { canCreateShortUrl } from '~/utils/permissions/shortUrlPermissions';
import { useAirStore } from '~/utils/ReduxUtils';
import { isRiveFile } from '~/utils/RiveUtils';
import { type OnRenameFunction } from './types';

const emptyArray: DropdownMenuOption[] = [];

type UseGetPrivateTableViewAssetMenuOptionsCallbackParams = {
  onRename?: OnRenameFunction;
};

export function useGetPrivateTableViewAssetMenuOptionsCallback({
  onRename,
}: UseGetPrivateTableViewAssetMenuOptionsCallbackParams = {}) {
  const onRenameRef = useRef<OnRenameFunction | undefined>(undefined);
  onRenameRef.current = onRename;
  const store = useAirStore();
  const { goToAssetPage } = useGoToAssetPage();
  const { canShowOpenInFinder } = useCanShowInFinderOption();
  const { isFlattenedView } = useIsFlattenedView();

  const { openClipInFinder } = useDesktopAppConnectionContext();
  const { toggleFavorite } = useToggleFavorite();
  const { downloadPrivateAsset } = useDownloadPrivateAsset();

  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { getClipPermissions } = useClipPermissionsCache();
  const { getBoardPermissions } = useBoardPermissionsCache();
  const { getAssetPermissions } = useAssetPermissionsCache();
  const { downloadPrivateProxyAsset } = useDownloadPrivateProxyAsset();
  const { canEditContextSources } = useCanEditContextSources();

  const { getCustomFieldsMenuOptions } = useGetPrivateCustomFieldsMenuOptions();
  const getAddToCollectionOption = useAddToCollectionOption();
  const { getClipMenuOptions } = useGetPrivateClipMenuOptions();

  const [showEditClipsTagsModal] = useAirModal(EditClipsTagsModal);
  const { showAddAssetsToBoardsModal } = useAddAssetsToBoardsModal();
  const { showRemoveAssetsFromBoardModal } = useRemoveAssetsFromBoardModal();
  const [showDeletePrivateClipsModal] = useAirModal(DeletePrivateClipsModal);
  const [sharePrivateClip] = useAirModal(SharePrivateClipModal);
  const [showMoveToBoardModal] = useAirModal(MoveItemsModal);
  const [showSetBoardThumbnailModal] = useAirModal(SetBoardThumbnailModal);
  const [showAddContextSourceClipModal] = useAirModal(PrivateContextSourcesAddClipModal);

  const getTableViewAssetMenuOptionsCallback: GetItemMenuOptionsCallback<Clip> = useCallback(
    ({ item: clip }) =>
      ({ trackLocation }) => {
        const currentBoardId = CentralizedBoardSelectors.boardId(store.getState());
        const currentBoardTitle = centralizedBoardTitleSelector(store.getState());
        const currentBoardPermissions = currentBoardId ? getBoardPermissions(currentBoardId) : undefined;

        const clipPermissions = getClipPermissions(clip.id);
        const assetPermissions = getAssetPermissions(clip.assetId);

        const canDownloadClip = canDownloadVersion(clipPermissions);
        const canRemoveAssets = currentBoardId && canRemoveAssetsFromBoard(currentBoardPermissions);
        const canRenameClip = canChangeVersionDetails(clipPermissions);
        const canMove = canMoveAsset(assetPermissions);
        const canShare = canCreateShortUrl(clipPermissions);
        const canEditTags = canEditClipTags(clipPermissions) && canRemoveClipTags(clipPermissions);
        const canDelete = canDeleteAsset(currentBoardId ? currentBoardPermissions : workspacePermissions);
        const canAddToBoard = canAddAssetToBoard(assetPermissions);
        const canManageApplications = canChangeAssetCustomFields(assetPermissions);

        if (!clipPermissions) {
          return emptyArray;
        }

        return getClipMenuOptions({
          customFieldsMenuOptions: canManageApplications ? getCustomFieldsMenuOptions({ assets: [clip] }) : undefined,
          addToCollectionOption: getAddToCollectionOption
            ? getAddToCollectionOption({ assetIds: [clip.assetId], boardIds: [] })
            : undefined,

          onAddToBoard: canAddToBoard
            ? () =>
                showAddAssetsToBoardsModal({
                  clipIds: [clip.id],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,

          onAddToContext: canEditContextSources
            ? () => {
                showAddContextSourceClipModal({
                  clip,
                });
              }
            : undefined,

          onEditTags: canEditTags
            ? () => {
                showEditClipsTagsModal({
                  clipIds: [clip.id],
                  trackLocation,
                  initialTags: clip.tags,
                });
              }
            : undefined,
          onDelete: canDelete
            ? () =>
                showDeletePrivateClipsModal({
                  assetIds: [clip.assetId],
                  trackLocation,
                  parentBoardId: currentBoardId,
                })
            : undefined,
          onDownload: canDownloadClip ? () => downloadPrivateAsset({ clipId: clip.id, trackLocation }) : undefined,
          originalResolution: canDownloadClip
            ? {
                id: 'original',
                label: `${clip.ext.toUpperCase()} - ${clip.width}x${clip.height}`,
                onSelect: () => downloadPrivateAsset({ clipId: clip.id, trackLocation }),
                type: 'item',
              }
            : undefined,
          altResolutions: canDownloadClip
            ? orderBy(clip.altResolutions, (resolution) => resolution.width * resolution.height, 'desc').map(
                (resolution) => ({
                  label: `${resolution.ext} - ${resolution.width}x${resolution.height}`,
                  id: `proxy-${resolution.width}-${resolution.height}`,
                  onSelect: () =>
                    downloadPrivateProxyAsset({ id: clip.id, resolutionId: resolution.id, trackLocation }),
                  type: 'item',
                }),
              )
            : undefined,
          onMoveToBoard:
            currentBoardId && canMove
              ? () =>
                  showMoveToBoardModal({
                    boards: [],
                    clips: [
                      {
                        id: clip.id,
                        assetId: clip.assetId,
                        openCommentCount: clip.openCommentCount,
                      },
                    ],
                    trackLocation,
                  })
              : undefined,
          onOpen: () =>
            goToAssetPage({
              asset: clip,
              trackLocation: 'open-dropdown-select',
            }),
          onOpenOnDesktop:
            /**
             * @see https://air-labs-team.slack.com/archives/C052RDYCGAG/p1696874696775379
             */
            canShowOpenInFinder && canDownloadClip
              ? () => {
                  openClipInFinder(clip, currentBoardId);
                }
              : undefined,
          onRemoveFromCurrentBoard:
            !isFlattenedView && currentBoardId && currentBoardTitle && canRemoveAssets
              ? () => {
                  showRemoveAssetsFromBoardModal({
                    boardId: currentBoardId,
                    boardTitle: currentBoardTitle,
                    clipIds: [clip.id],
                    trackLocation,
                  });
                }
              : undefined,
          onRenameAssetInline: canRenameClip ? () => onRenameRef.current?.() : undefined,
          onSetBoardThumbnail:
            ClipUtils.canHavePreviewImage(clip.type) && !isRiveFile(clip)
              ? () =>
                  showSetBoardThumbnailModal({
                    trackLocation: 'asset-details',
                    clip,
                  })
              : undefined,
          onShare: canShare
            ? () =>
                sharePrivateClip({
                  clip,
                  trackLocation,
                  boardContext: currentBoardId,
                })
            : undefined,

          onFavorite: !clip.bookmarked ? () => toggleFavorite({ clips: [clip], trackLocation }) : undefined,
          onUnfavorite: clip.bookmarked ? () => toggleFavorite({ clips: [clip], trackLocation }) : undefined,
        });
      },
    [
      store,
      getBoardPermissions,
      getClipPermissions,
      getAssetPermissions,
      workspacePermissions,
      getClipMenuOptions,
      getCustomFieldsMenuOptions,
      canShowOpenInFinder,
      isFlattenedView,
      showAddAssetsToBoardsModal,
      showEditClipsTagsModal,
      showDeletePrivateClipsModal,
      downloadPrivateAsset,
      downloadPrivateProxyAsset,
      showMoveToBoardModal,
      goToAssetPage,
      openClipInFinder,
      showRemoveAssetsFromBoardModal,
      showSetBoardThumbnailModal,
      sharePrivateClip,
      toggleFavorite,
      getAddToCollectionOption,
      canEditContextSources,
      showAddContextSourceClipModal,
    ],
  );

  return {
    getTableViewAssetMenuOptionsCallback,
  };
}
